// agent-parts-md.js — minimal markdown renderer, no external deps.
// Split out of agent-activity.js so agent-parts.js can import renderMarkdown
// without creating a circular dependency (agent-parts.js → agent-activity.js).
//
// Consumers:
//   import { renderMarkdown, renderInline } from "./agent-parts-md.js";

import { highlightCode } from "./highlight.js";

function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]));
}

export function renderInline(text) {
  // Escape first, then re-introduce structure
  let s = esc(text);

  // File paths → blue underline links (before inline-code so a path inside backticks still works)
  s = s.replace(/(?<![\w/])(tenant|knowledge|sap-help)\/[\w./-]+/g, m => {
    const base = m.split("/").pop();
    // No inline onclick — a delegated listener in main.js handles clicks on a.md-flink[data-path].
    // Emitting no inline handler keeps the renderer CSP-friendly and removes the fragile
    // string-interpolated handler (the path is opened from data-path, not spliced into JS).
    return `<a class="md-flink" data-path="${m}" title="${m}" href="#">${base}</a>`;
  });

  // Inline code (backticks)
  s = s.replace(/`([^`]+)`/g, '<code class="md-code-inline">$1</code>');

  // Bold
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // Italic (single asterisk or underscore, avoid matching across spaces or doubles)
  s = s.replace(/(^|[\s(])\*([^*\s][^*]*[^*\s]|[^*\s])\*(?=$|[\s).,!?;:])/g, "$1<em>$2</em>");

  return s;
}

// Map a fenced-code language token to the language the shared highlighter
// (highlight.js:highlightCode) understands, or null when unsupported (plain
// escaped code, unchanged behavior). Kept pure + exported so it's unit-testable
// in plain node — renderMarkdown's code path itself needs a DOM (highlightCode
// escapes via state.esc(), which uses document).
export function highlightLangFor(fence) {
  const t = String(fence ?? "").trim().toLowerCase();
  if (t === "python" || t === "py" || t === "python3" || t === "ironpython" || t === "ipy") return "python";
  if (t === "html" || t === "htm" || t === "cshtml" || t === "razor") return "html";
  if (t === "json") return "json";
  if (t === "xml") return "xml";
  return null;
}

export function renderMarkdown(md) {
  if (!md) return "";
  const lines = String(md).split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (/^```/.test(line)) {
      const lang = line.replace(/^```/, "").trim();
      i++;
      const code = [];
      while (i < lines.length && !/^```/.test(lines[i])) { code.push(lines[i]); i++; }
      i++;
      const rawCode = code.join("\n");
      // Highlight only for languages the shared tokenizer understands; otherwise
      // fall back to plain, locally-escaped code (unchanged output). highlightCode()
      // escapes every token via state.esc(), so the emitted spans are XSS-safe —
      // the same guarantee as the legacy createCodeBlock path in chat.js.
      const hlLang = highlightLangFor(lang);
      const codeHtml = hlLang ? highlightCode(rawCode, hlLang) : esc(rawCode);
      const langClass = lang ? " lang-" + esc(lang) : "";
      // Wrap so the delegated listener (bottom of file) can attach a Copy button
      // per block. Preserves .md-code so existing CSS still applies.
      out.push(
        `<div class="md-code-wrap">` +
          `<button type="button" class="md-copy-btn" aria-label="Copy code">Copy</button>` +
          `<pre class="md-code${langClass}"><code>${codeHtml}</code></pre>` +
        `</div>`
      );
      continue;
    }

    // Heading
    const hMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (hMatch) {
      const lvl = hMatch[1].length;
      out.push(`<h${lvl} class="md-h md-h${lvl}">${renderInline(hMatch[2])}</h${lvl}>`);
      i++;
      continue;
    }

    // Bullet list (consecutive lines starting with - or *)
    if (/^[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*+]\s+/, ""));
        i++;
      }
      out.push("<ul class=\"md-ul\">" + items.map(it => `<li>${renderInline(it)}</li>`).join("") + "</ul>");
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ""));
        i++;
      }
      out.push("<ol class=\"md-ol\">" + items.map(it => `<li>${renderInline(it)}</li>`).join("") + "</ol>");
      continue;
    }

    // Table (header row | divider row | body rows)
    // Body rows accept lines starting with `|` whether or not they end with `|` —
    // some LLMs truncate the trailing pipe.
    if (/^\|.*\|/.test(line) && i + 1 < lines.length && /^\|[\s|:-]+\|?\s*$/.test(lines[i + 1])) {
      const splitCells = (row) => row.replace(/^\|/, "").replace(/\|\s*$/, "").split("|").map(c => c.trim());
      const headers = splitCells(line);
      i += 2; // skip header + divider
      const rows = [];
      while (i < lines.length && /^\|/.test(lines[i]) && lines[i].trim() !== "") {
        rows.push(splitCells(lines[i]));
        i++;
      }
      const thead = "<thead><tr>" + headers.map(h => `<th>${renderInline(h)}</th>`).join("") + "</tr></thead>";
      const tbody = "<tbody>" + rows.map(r =>
        "<tr>" + r.map(c => `<td>${renderInline(c)}</td>`).join("") + "</tr>"
      ).join("") + "</tbody>";
      out.push(`<div class="md-table-wrap"><table class="md-table">${thead}${tbody}</table></div>`);
      continue;
    }

    // Blank line → paragraph break
    if (line.trim() === "") { i++; continue; }

    // Paragraph (consume contiguous non-empty non-special lines)
    const para = [line];
    i++;
    while (i < lines.length && lines[i].trim() !== "" && !/^(#{1,6})\s+/.test(lines[i]) && !/^[-*+]\s+/.test(lines[i]) && !/^\d+\.\s+/.test(lines[i]) && !/^```/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    out.push(`<p class="md-p">${renderInline(para.join(" "))}</p>`);
  }
  return out.join("\n");
}

// ---------------------------------------------------------------------------
// Copy-to-clipboard for rendered code blocks.
// renderMarkdown() returns a string its consumers insert via innerHTML, so a
// per-button handler can't be bound at render time. One delegated listener,
// registered once when this module loads, serves every .md-copy-btn. The raw
// code is read from the sibling <code>'s textContent (the browser decodes
// entities and drops highlight spans), so nothing is duplicated into an
// attribute and there is no new escaping surface. Guarded for non-DOM (node)
// import so the renderer stays unit-testable.
// ---------------------------------------------------------------------------
if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener("click", (ev) => {
    const target = ev.target;
    const btn = target && target.closest ? target.closest(".md-copy-btn") : null;
    if (!btn) return;
    const wrap = btn.closest(".md-code-wrap");
    const codeEl = wrap ? wrap.querySelector("pre.md-code > code") : null;
    if (!codeEl) return;
    const raw = codeEl.textContent || "";
    const reset = () => setTimeout(() => {
      btn.textContent = "Copy";
      btn.classList.remove("is-copied");
    }, 1200);
    try {
      const p = navigator.clipboard && navigator.clipboard.writeText(raw);
      if (p && typeof p.then === "function") {
        p.then(
          () => { btn.textContent = "Copied"; btn.classList.add("is-copied"); reset(); },
          () => { btn.textContent = "Failed"; reset(); },
        );
      } else {
        btn.textContent = "Copied";
        btn.classList.add("is-copied");
        reset();
      }
    } catch (_err) {
      btn.textContent = "Failed";
      reset();
    }
  });
}
